import { can } from '../../permissions';
import SectionButtons from '../ReusableComponents/SectionButtons';
import ReviewDecisionModal from './components/ReviewDecisionModal';
import { MdRefresh, MdEdit } from 'react-icons/md';
import FormFileAnswer from './components/FormFileAnswer';

import PortalButton from './components/PortalButton';
import React, { useState } from 'react';
import OperationPanel from './components/OperationPanel';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import moment from 'moment';
import { useInscricaoAdmin, usePortalMutation, usePortal } from './hooks';
import PortalLayout from './components/PortalLayout';
import DocumentReview from './components/DocumentReview';
import AppealReview from './components/AppealReview';
import { ErrorNotice, Status, Loading, Empty } from './components/Feedback';
export default function PortalApplication() {
  const { user } = usePortal();
  const [reviewing, setReviewing] = useState(false);
  const location = useLocation(),
    history = useHistory(),
    params = new URLSearchParams(location.search);
  const modules = [
    {
      value: 'dados',
      label: 'Dados da inscrição',
      description: 'Cadastro e situação',
      icon: 'candidatos',
    },
    {
      value: 'documentos',
      label: 'Documentos',
      description: 'Arquivos e análise documental',
      icon: 'conteudos',
    },
    {
      value: 'recursos',
      label: 'Recursos',
      description: 'Solicitações e respostas',
      icon: 'mensagens',
    },
    { value: 'historico', label: 'Histórico', description: 'Eventos da inscrição', icon: 'logs' },
  ];
  const value = modules.some((item) => item.value === params.get('modulo'))
    ? params.get('modulo')
    : 'dados';
  const { application } = useParams(),
    query = useInscricaoAdmin(application),
    decision = usePortalMutation(`inscricoes/${application}/decisao`),
    retry = usePortalMutation(`inscricoes/${application}/reprocessar`);
  const app = query.data;
  return (
    <PortalLayout
      title={app?.user.name || 'Inscrição'}
      description={
        app ? app.selection.title + ' · ' + app.position.title : 'Consulta e análise da inscrição.'
      }
    >
      <ErrorNotice error={query.error} />
      {query.isLoading ? (
        <Loading />
      ) : (
        app && (
          <>
            <section className="card module-card detail-summary hidethis">
              <div className="card-body">
                <h2 className="module-selector-title">Escolha uma seção</h2>
                <SectionButtons
                  compact
                  variant="modules"
                  items={modules}
                  value={value}
                  onChange={(module) => {
                    params.set('modulo', module);
                    history.push({ pathname: location.pathname, search: params.toString() });
                  }}
                />
              </div>
            </section>
            {value === 'dados' && (
              <section className="card module-card module-content">
                <div className="card-body">
                  <div className="portal-admin-row">
                    <div className="module-content-heading">
                      <h2>Dados da inscrição</h2>
                      <p>Identificação, situação e informações enviadas pelo candidato.</p>
                    </div>
                    {app.status === 'approved' &&
                      !['synced', 'failed'].includes(app.integration_status) &&
                      can(user, 'portal_integracoes', 'edit') && (
                        <OperationPanel
                          single
                          filters={{ number: app.protocol }}
                          selected={[app.id]}
                        />
                      )}
                  </div>
                  <p>
                    Protocolo: <strong>{app.protocol}</strong>
                  </p>
                  {app.form_snapshot && (
                    <dl className="portal-admin-facts">
                      {app.form_snapshot.map((f) => (
                        <div key={f.id}>
                          <dt>{f.label}</dt>
                          <dd>
                            {f.type === 'file' ? (
                              <FormFileAnswer application={app} field={f} />
                            ) : (
                              String(app.data?.[f.id] ?? '—')
                            )}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  )}
                  <div className="portal-admin-facts">
                    <div>
                      <small>Nome</small>
                      <strong>{app.user.name}</strong>
                    </div>
                    <div>
                      <small>E-mail</small>
                      <strong>{app.user.email}</strong>
                    </div>
                    <div>
                      <small>CPF</small>
                      <strong>{app.user.cpf}</strong>
                    </div>
                    <div>
                      <small>Nascimento</small>
                      <strong>{moment(app.user.birth_date).format('DD/MM/YYYY')}</strong>
                    </div>
                    <div>
                      <small>Número de inscrição</small>
                      <strong>{app.registration_number || 'Aguardando vinculação'}</strong>
                    </div>
                    <div>
                      <small>Situação</small>
                      <Status value={app.status} />
                    </div>
                  </div>
                  <p>
                    Vinculação com a OnTest: <Status value={app.integration_status} />
                  </p>
                  {app.integration?.last_error && (
                    <div className="alert alert-warning" role="status">
                      {app.integration.last_error}
                      <small className="d-block">Tentativas: {app.integration.attempts}</small>
                    </div>
                  )}
                  {app.decision_reason && (
                    <div className="alert alert-info">{app.decision_reason}</div>
                  )}
                  {app.integration_status === 'failed' && (
                    <>
                      <ErrorNotice error={retry.error} />
                      <PortalButton
                        className="btn btn-primary hidethis"
                        disabled={retry.isLoading || !retry.allowed}
                        onClick={() => retry.mutate({})}
                        type="button"
                      >
                        <MdRefresh />
                        Reprocessar vinculação
                      </PortalButton>
                    </>
                  )}
                  {['under_review', 'documents_pending', 'rejected'].includes(app.status) && (
                    <>
                      <PortalButton
                        className="btn btn-info hidethis"
                        disabled={!decision.allowed}
                        onClick={() => {
                          decision.reset();
                          setReviewing(true);
                        }}
                      >
                        <MdEdit /> Analisar inscrição
                      </PortalButton>
                      {reviewing && (
                        <ReviewDecisionModal
                          title="Analisar inscrição"
                          description={app.user.name}
                          mutation={decision}
                          onClose={() => setReviewing(false)}
                          options={[
                            ['approved', 'Deferir inscrição'],
                            ['documents_pending', 'Solicitar documentos'],
                            ['rejected', 'Indeferir inscrição'],
                          ]}
                        />
                      )}
                    </>
                  )}
                </div>
              </section>
            )}
            {value === 'documentos' && (
              <section className="card module-card module-content">
                <div className="card-body">
                  <h2 className="module-details-title">Documentos</h2>
                  {app.documents.length === 0 ? (
                    <Empty>Nenhum documento enviado.</Empty>
                  ) : (
                    app.documents.map((d) => (
                      <DocumentReview key={d.id} application={app} document={d} />
                    ))
                  )}
                </div>
              </section>
            )}
            {value === 'recursos' && (
              <section className="card module-card module-content">
                <div className="card-body">
                  <h2 className="module-details-title">Recursos</h2>
                  {app.appeals.length === 0 ? (
                    <Empty>Nenhum recurso apresentado.</Empty>
                  ) : (
                    app.appeals.map((a) => <AppealReview key={a.id} application={app} appeal={a} />)
                  )}
                </div>
              </section>
            )}
            {value === 'historico' && (
              <section className="card module-card module-content">
                <div className="card-body">
                  <h2 className="module-details-title">Histórico</h2>
                  {app.events.length === 0 ? (
                    <Empty>Nenhum registro encontrado.</Empty>
                  ) : (
                    <ol className="portal-admin-timeline">
                      {app.events.map((e) => (
                        <li key={e.id}>
                          {e.message}
                          <small>{moment(e.created_at).format('DD/MM/YYYY HH:mm')}</small>
                        </li>
                      ))}
                    </ol>
                  )}
                </div>
              </section>
            )}
          </>
        )
      )}
    </PortalLayout>
  );
}
